import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, firstValueFrom, of, throwError } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { environment } from 'src/environments/environment';
import { ApiService } from './api.service';
import { ClockRegisterService } from './clock-register.service';
import { TokenService } from './token.service';
import { UserService } from './user.service';
import { EmployeeService } from './employee.service';
import { PusherService } from './pusher.service';
import { RecordingService } from './recording.service';
import { OauthService, OauthTokenResponse } from './oauth.service';
import { PlatformService } from './platform.service';
import { User } from '../models/user.model';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private token = inject(TokenService);
  private api = inject(ApiService);
  private userService = inject(UserService);
  private employee = inject(EmployeeService);
  private dialog = inject(MatDialog);
  private clock = inject(ClockRegisterService);
  private pusher = inject(PusherService);
  private oauth = inject(OauthService);
  private recording_service = inject(RecordingService);
  private platform = inject(PlatformService);
  private router = inject(Router);

  private loggedIn = new BehaviorSubject<boolean>(false);
  private refresh_in_flight: Observable<boolean> | null = null;
  private logging_out = false;

  startOauthLogin(redirect: string | null = null): void {
    this.logging_out = false;
    this.oauth.beginLogin(redirect);
  }

  completeOauthLogin(code: string, state: string): Observable<User> {
    this.logging_out = false;

    return this.oauth.exchangeCode(code, state).pipe(
      switchMap((token_response: OauthTokenResponse) => {
        this.token.handle(token_response.access_token, token_response.refresh_token ?? null);
        return this.api.authMe().pipe(
          map(user => {
            this.onLoginSuccess({ access_token: token_response.access_token, user });
            return user;
          })
        );
      }),
      catchError(error => {
        this.oauth.clearPkceSession();
        return throwError(() => error);
      })
    );
  }

  logout(query: Record<string, string> = {}): void {
    if (this.logging_out) {
      return;
    }

    this.logging_out = true;

    if (query.redirect && !query.redirect.startsWith('/auth')) {
      this.oauth.rememberPostLoginRedirect(query.redirect);
    }

    const finish = (): void => {
      this.clearLocalSession();
      this.redirectToAuthServerLogout(query);
    };

    if (this.token.hasAttachableAccessToken()) {
      this.api.logout().subscribe({ next: finish, error: finish });
      return;
    }

    finish();
  }

  refreshToken(): Observable<boolean> {
    if (this.refresh_in_flight) {
      return this.refresh_in_flight;
    }

    const refresh_token = this.token.getRefreshToken();
    if (!refresh_token) {
      return of(false);
    }

    this.refresh_in_flight = this.oauth.refresh(refresh_token).pipe(
      switchMap((token_response: OauthTokenResponse) => {
        this.token.handle(token_response.access_token, token_response.refresh_token ?? refresh_token);

        if (this.userService.getCurrentUser()) {
          this.changeAuthStatus(true);
          return of(true);
        }

        return this.api.authMe().pipe(
          map(user => {
            this.onLoginSuccess({ access_token: token_response.access_token, user });
            return true;
          })
        );
      }),
      catchError(() => of(false)),
      finalize(() => {
        this.refresh_in_flight = null;
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );

    return this.refresh_in_flight;
  }

  isLoggedIn(): boolean {
    return this.loggedIn.value;
  }

  isLoggingOut(): boolean {
    return this.logging_out;
  }

  hasStoredCredentials(): boolean {
    return this.token.get() != null || this.token.getRefreshToken() != null;
  }

  isImpersonating(): boolean {
    return this.token.getParent() != null;
  }

  ensureSession(): Promise<boolean> {
    if (this.token.get() == null && this.token.getRefreshToken() == null) {
      return Promise.resolve(false);
    }

    if (this.token.loggedIn() && this.isLoggedIn()) {
      return Promise.resolve(true);
    }

    if (this.token.loggedIn() && !this.isLoggedIn()) {
      return firstValueFrom(
        this.api.authMe().pipe(
          map(user => {
            this.onLoginSuccess({ access_token: this.token.get() as string, user });
            return true;
          }),
          catchError(() => this.refreshToken())
        )
      );
    }

    return firstValueFrom(this.refreshToken());
  }

  public impersonate(user_id: number) {
    return this.api.impersonate({ user_id: user_id }).pipe(
      map(response => {
        if (response.user && response.access_token) {
          this.userService.setParentUser(this.userService.getCurrentUser());
          this.token.handleParent(this.token.get() as string);

          this.token.handle(response.access_token, response.refresh_token ?? null);
          this.onLoginSuccess(response);
          this.employee.sendUpdateHoursTopMenu();
        }
        return true;
      })
    );
  }

  public desimpersonate() {
    this.token.restoreParentSession();
    this.userService.setUser(this.userService.getCurrentParentUser());
    this.userService.deleteParentUser();
    this.clock.init();
    this.employee.sendUpdateHoursTopMenu();
    this.changeAuthStatus(true);
  }

  private clearLocalSession(): void {
    this.token.remove();
    this.token.removeParent();
    this.userService.deleteUser();
    this.userService.deleteParentUser();
    this.employee.logout();
    this.clock.reset();
    this.pusher.unsubscribeFromPusher();
    this.recording_service.clearSessionOnLogout();
    this.dialog.closeAll();
    this.changeAuthStatus(false);
  }

  private redirectToAuthServerLogout(query: Record<string, string> = {}): void {
    const params = new URLSearchParams();
    Object.entries(query).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    const query_string = params.toString();
    const suffix = query_string ? `?${query_string}` : '';

    const post_logout_redirect = this.platform.isElectronApp()
      ? `${environment.electron_schema}://auth/login${suffix}`
      : `${environment.angular_url}/auth/login${suffix}`;

    const logout_url = `${environment.auth_url}/logout-session?redirect=${encodeURIComponent(post_logout_redirect)}`;

    if (this.platform.isElectronApp()) {
      window.electron.openExternalLink(logout_url);
      void this.router.navigate(['/auth/login'], { queryParams: query });
      return;
    }

    window.location.href = logout_url;
  }

  private changeAuthStatus(value: boolean) {
    this.loggedIn.next(value);
  }

  private onLoginSuccess(response: { access_token: string; user: User }) {
    this.logging_out = false;

    if (!this.token.get()) {
      this.token.handle(response.access_token);
    }
    this.userService.setUser(response.user);
    this.clock.init();
    this.pusher.initUser(response.user);
    this.changeAuthStatus(true);
  }
}
